'use client';

import { SubscribeButton } from '@/components/billing/SubscribeButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import {
  getSubscriptionStatus,
  isTrialing,
  shouldShowAccessUntil,
  shouldShowCanceledOn,
  shouldShowNextBilling,
} from '@/lib/billing/subscriptionStatus';

type Subscription = {
  status?: string;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: string | null;
  currentPeriodEndedAt: string;
  amountCents?: number;
  seatCount?: number;
};

type BillingSummary = {
  trialEndedAt?: string | null;
  memberCount?: number;
};

type SubscriptionCardProps = {
  subscription: Subscription | null | undefined;
  billing: BillingSummary | null | undefined;
  onCancelSubscription: () => void;
  isAdmin?: boolean;
};

export function SubscriptionCard({
  subscription,
  billing,
  onCancelSubscription,
  isAdmin = false,
}: SubscriptionCardProps) {
  const status = getSubscriptionStatus(subscription, billing);
  const showTrialEnd = isTrialing(billing);
  const showNextBilling = shouldShowNextBilling(subscription);
  const showAccessUntil = shouldShowAccessUntil(subscription);
  const showCanceledOn = shouldShowCanceledOn(subscription);
  const isCanceled =
    subscription?.cancelAtPeriodEnd || subscription?.canceledAt;
  const isActive = subscription?.status === 'active';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription</CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Status</span>
            <span>{status}</span>
          </div>

          {showTrialEnd && billing?.trialEndedAt && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">
                {isCanceled ? 'Trial Ends' : 'Billing Starts'}
              </span>
              <span>{new Date(billing.trialEndedAt).toLocaleDateString()}</span>
            </div>
          )}

          {showNextBilling && (
            <>
              <div className="flex items-start justify-between gap-4">
                <span className="text-muted-foreground">Next Billing</span>
                <span className="text-right">
                  {subscription?.currentPeriodEndedAt
                    ? new Date(
                        subscription.currentPeriodEndedAt
                      ).toLocaleDateString()
                    : '—'}
                </span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <span className="text-muted-foreground">Amount</span>
                <span className="text-right">
                  {subscription?.amountCents && subscription.amountCents > 0
                    ? new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(subscription.amountCents / 100)
                    : '—'}
                </span>
              </div>
            </>
          )}

          {showAccessUntil && subscription?.currentPeriodEndedAt && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Access Until</span>
              <span>
                {new Date(
                  subscription.currentPeriodEndedAt
                ).toLocaleDateString()}
              </span>
            </div>
          )}

          {showCanceledOn && subscription?.canceledAt && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Canceled On</span>
              <span>
                {new Date(subscription.canceledAt).toLocaleDateString()}
              </span>
            </div>
          )}

          <div className="flex items-start justify-between gap-4">
            <span className="text-muted-foreground">Seats</span>
            <span className="text-right">{subscription?.seatCount ?? '—'}</span>
          </div>

          {isAdmin && (
            <div className="space-y-3 pt-4">
              {isCanceled ? (
                <SubscribeButton
                  variant="outline"
                  className="w-full"
                  wasPreviouslySubscribed={true}
                />
              ) : isActive ? (
                <button
                  onClick={onCancelSubscription}
                  className="text-muted-foreground hover:text-foreground cursor-pointer text-sm underline transition-colors"
                >
                  Cancel subscription
                </button>
              ) : showTrialEnd ? (
                // Trialing with payment method on file — Stripe auto-converts
                // to a paid subscription at trial end, so the meaningful action
                // here is canceling the trial (which also cancels the upcoming
                // first charge), not subscribing.
                <button
                  onClick={onCancelSubscription}
                  className="text-muted-foreground hover:text-foreground cursor-pointer text-sm underline transition-colors"
                >
                  Cancel trial
                </button>
              ) : (
                <SubscribeButton
                  variant="outline"
                  className="w-full"
                  wasPreviouslySubscribed={false}
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
